import { FC } from 'react';
// eslint-disable-next-line no-duplicate-imports
import type { ComponentProps } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Locale } from 'react-datepicker/dist/date_utils';
import translation from '../../languages';
import classNames from '../../utils/classnames';
import isMainWorld from '../../utils/is-main-world';
import { DAYS, MONTHS } from './date-picker.constants';
import './date-picker.scss';

// type Picker = ComponentProps<typeof ReactDatePicker>;

type DatePickerProps = ComponentProps<typeof ReactDatePicker> &
    TranslationProps & {
        id?: string;
        dateFormat?: string;
        className?: string;
        hasError?: boolean;
        isMonthYearPicker?: boolean;
        dropdownNavigation?: boolean;
    };

const DROPDOWN_NAV_MIN_DATE = new Date(1900, 0, 1);
const DROPDOWN_NAV_MAX_DATE = new Date(2100, 11, 31);

const DatePicker: FC<DatePickerProps> = ({
    className = '',
    hasError = false,
    isMonthYearPicker,
    dropdownNavigation = false,
    calendarClassName,
    t,
    ...props
}) => {
    const locale = {
        localize: {
            day: (n: number) => t(`common.days.${DAYS[n]}`),
            month: (n: number) => t(`common.months.${MONTHS[n]}`)
        },
        formatLong: {
            date: () => 'mm/dd/yyyy'
        },
        match: {
            day: (pattern: string) => {
                const dayIndex = DAYS.findIndex((day) => day.toLowerCase() === pattern.toLowerCase());

                return dayIndex !== -1 ? { value: dayIndex } : null;
            },
            month: (pattern: string) => {
                const monthIndex = MONTHS.findIndex((month) => month.toLowerCase() === pattern.toLowerCase());

                return monthIndex !== -1 ? { value: monthIndex } : null;
            }
        }
    };

    const dropdownNavProps = dropdownNavigation
        ? {
              showMonthDropdown: true,
              showYearDropdown: true,
              dropdownMode: 'select' as const,
              fixedHeight: true,
              minDate: props.minDate ?? DROPDOWN_NAV_MIN_DATE,
              maxDate: props.maxDate ?? DROPDOWN_NAV_MAX_DATE
          }
        : {};

    return (
        <ReactDatePicker
            locale={isMainWorld() ? (locale as unknown as Locale) : undefined}
            autoComplete='off'
            className={classNames(`datepicker ${className}`, { 'error-field': hasError })}
            calendarClassName={classNames(calendarClassName, { 'datepicker-dropdown-nav': dropdownNavigation })}
            showMonthYearPicker={isMonthYearPicker}
            {...props}
            {...dropdownNavProps}
        />
    );
};

const translate = translation('common');

export default translate(DatePicker);
